// Lead source = where a referral/patient case originated. Admin/internal-only —
// it is shown in the internal dashboard but NEVER in the referring-dentist portal.
//
// Auto-tagged by entry path:
//   Website  → `website_form`   (referral-public-submit edge fn)
//   Portal   → `external_portal` (submit_external_referral RPC / column default)
//   Internal → `internal`        (hand-keyed by clinic staff / internal doctors)
//   WhatsApp → `whatsapp`        (hand-keyed by front desk from a WhatsApp referral)
//
// Legacy rows use website_form / external_portal; the label map keeps them working.

export type LeadSource = 'website_form' | 'external_portal' | 'internal' | 'whatsapp';

const LEAD_SOURCE_LABELS: Record<string, string> = {
  website_form: 'Website',
  website: 'Website',
  external_portal: 'Portal',
  portal: 'Portal',
  internal: 'Internal Doctors',
  internal_doctor: 'Internal Doctors',
  whatsapp: 'WhatsApp',
};

export const formatLeadSource = (source?: string | null): string => {
  if (!source) return 'Unknown';
  return (
    LEAD_SOURCE_LABELS[source] ??
    source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

// Default for a NEW hand-keyed case: WhatsApp (most manual leads arrive that way).
export const DEFAULT_MANUAL_LEAD_SOURCE: LeadSource = 'whatsapp';

// Selectable when hand-keying a case. Website/Portal are auto-tagged by their
// entry path and are offered here only so an admin can correct a mislabel.
export const LEAD_SOURCE_OPTIONS: Array<{ value: LeadSource; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'internal', label: 'Internal Doctors' },
  { value: 'website_form', label: 'Website' },
  { value: 'external_portal', label: 'Portal' },
];

// Tailwind badge classes per source (admin-only display).
export const leadSourceBadgeClass = (source?: string | null): string => {
  switch (source) {
    case 'website_form':
    case 'website':
      return 'bg-blue-100 text-blue-800';
    case 'external_portal':
    case 'portal':
      return 'bg-purple-100 text-purple-800';
    case 'internal':
    case 'internal_doctor':
      return 'bg-amber-100 text-amber-800';
    case 'whatsapp':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};
